// LapsedCookBanner — closure prompt for cooks that outlived their
// live-banner window without being finished OR abandoned.
//
// The rule this enforces: A TIMER NEVER CLOSES A COOK — ONLY THE USER
// DOES. Before this, a session past its window simply stopped
// rendering: no cook_log, no prompt, nothing. Three days of pancakes
// silently never made it into history. Now the session surfaces here
// (for up to 7 days) until the user picks a side:
//
//   FINISH & LOG  → resumes the cook; one tap from there into the
//                   post-cook flow logs it with XP + kitchen update.
//   DIDN'T COOK   → honest abandon (status flip, recorded).
//   ✕             → snooze for this app session only; it returns.
//
// Renders ONLY when the live CookBanner is absent (App gates it) so
// the two never stack; writes the same --cook-banner-h CSS variable
// contract so page content is pushed down, not covered.
//
// Amber register on purpose — this is a watch-out state, and gold/
// amber is the house tone for "needs your attention."

import { useEffect, useRef } from "react";
import { FONT, COLOR } from "../lib/tokens";

function agoLabel(startedAt) {
  const t = startedAt ? new Date(startedAt).getTime() : 0;
  if (!t) return "";
  const hours = Math.floor((Date.now() - t) / 3600000);
  if (hours < 24) return `${Math.max(1, hours)}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
}

export default function LapsedCookBanner({ lapsed = [], onResume, onDiscard, onDismiss }) {
  const first = lapsed[0] || null;
  const ref = useRef(null);

  // Same page-offset contract as CookBanner: write the rendered
  // height into --cook-banner-h so content below is pushed down by
  // exactly this bar. Removed on hide so non-lapsed sessions stay
  // full-height. (App guarantees the live banner isn't also mounted.)
  useEffect(() => {
    if (!first) return undefined;
    const h = ref.current ? ref.current.offsetHeight : 0;
    if (h > 0) {
      document.documentElement.style.setProperty("--cook-banner-h", `${h}px`);
    }
    return () => {
      document.documentElement.style.removeProperty("--cook-banner-h");
    };
  }, [first, lapsed.length]);

  if (!first) return null;

  const title = first.session?.recipe_title || first.recipe?.title || "your last cook";
  const more = lapsed.length - 1;

  return (
    <div
      ref={ref}
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 95,
        paddingTop: "env(safe-area-inset-top)",
        background: "rgba(46,36,14,0.97)",
        borderBottom: `1px solid ${COLOR.goldDim}`,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
    >
      <div style={{ maxWidth: 480, margin: "0 auto", padding: "10px 14px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
              color: COLOR.gold, letterSpacing: "0.12em",
            }}>
              UNFINISHED COOK · {agoLabel(first.session?.started_at).toUpperCase()}
              {more > 0 ? ` · +${more} MORE` : ""}
            </div>
            <div style={{
              fontFamily: FONT.serif, fontStyle: "italic", fontSize: 17,
              // Banner background is a fixed dark olive — light cream,
              // NOT COLOR.ink (which is near-black body text for light
              // surfaces and renders black-on-black here).
              color: COLOR.night, marginTop: 2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              Did you finish {title}?
            </div>
          </div>
          <button
            type="button"
            aria-label="Snooze this prompt"
            className="mcm-focusable"
            onClick={() => onDismiss?.(first.session?.id)}
            style={{
              width: 30, height: 30, flexShrink: 0, borderRadius: 15,
              background: "transparent", border: "1px solid rgba(250,246,238,0.28)",
              color: "rgba(250,246,238,0.7)", fontFamily: FONT.mono, fontSize: 13,
              cursor: "pointer", touchAction: "manipulation",
            }}
          >
            ×
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 9 }}>
          <button
            type="button"
            className="mcm-focusable"
            onClick={() => onResume?.(first.recipe, first.session)}
            style={{
              flex: 2, padding: "10px",
              background: COLOR.leaf, border: "none", borderRadius: 10,
              fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
              color: COLOR.night, letterSpacing: "0.08em",
              cursor: "pointer", touchAction: "manipulation",
            }}
          >
            FINISH &amp; LOG IT →
          </button>
          <button
            type="button"
            className="mcm-focusable"
            onClick={() => onDiscard?.(first.session?.id)}
            style={{
              flex: 1, padding: "10px",
              background: "transparent", border: "1px solid rgba(250,246,238,0.28)",
              borderRadius: 10, fontFamily: FONT.mono, fontSize: 12,
              color: "rgba(250,246,238,0.72)", letterSpacing: "0.06em",
              cursor: "pointer", touchAction: "manipulation",
            }}
          >
            DIDN'T COOK IT
          </button>
        </div>
      </div>
    </div>
  );
}
